// redux utils
import { createAsyncThunk } from '@reduxjs/toolkit';
// api
import * as ppa from './api/ppa';
import * as srmsApi from './api/srmsReports';
import { toReportTo, fromReportTo, fromMetadata, ppaReportType, SOURCE } from './api/srmsAdapter';

// INSTISSP-1819 — SSP reports live in SRMS, FAM in PPA. List merges both (dedup by uuid, SRMS wins);
// create/preview route by report type, everything else by the report's tagged _source.

// Look up a loaded report's backend. Falls back to PPA when it isn't in the list yet.
const sourceOf = (getState, reportUUID) => {
    const list = getState().report.allReports || [];
    return list.find(r => r.reportUUID === reportUUID)?._source || SOURCE.PPA;
};

// filename="foo.csv" out of a Content-Disposition header.
const filenameFromDisposition = (disposition) => {
    if (!disposition) return null;
    const part = disposition.split(';').find(p => p.includes('filename'));
    return part ? part.replace(/.*filename=/, '').replace(/"/g, '').trim() : null;
};

// SRMS list = metadata only (enough for the table); full config is hydrated lazily on edit/download.
const fetchSrmsReports = async () => {
    const { data } = await srmsApi.getReports({ limit: 200 });
    const metas = (data && data.data) || [];
    return metas.map(fromMetadata);
};

// thunks
export const fetchAllReportsByAccount = createAsyncThunk('report/getAllReportsByAccount', async accountUUID => {
    const [ppaRes, srmsRes] = await Promise.allSettled([
        ppa.getAllReportsByAccount(accountUUID),
        fetchSrmsReports(),
    ]);

    const ppaList = ppaRes.status === 'fulfilled'
        ? (ppaRes.value.data || []).map(r => ({ ...r, _source: SOURCE.PPA, reportProductType: ppaReportType(r.metrics) }))
        : [];
    const srmsList = srmsRes.status === 'fulfilled' ? srmsRes.value : [];

    if (srmsRes.status === 'rejected') {
        // SRMS unreachable (e.g. url.srms unset): show PPA reports only so the page loads
        console.warn('[SRMS] report list unavailable, showing PPA reports only:', srmsRes.reason && srmsRes.reason.message);
    }

    const srmsUuids = new Set(srmsList.map(r => r.reportUUID));
    // Newest first, as the PPA-only list was (REPORT_V2 ... ORDER BY created_at DESC). SRMS returns its own
    // order (by name), so without this every SRMS report sat above every PPA one.
    const createdAtMs = r => {
        const t = r.createdAt ? new Date(r.createdAt).getTime() : NaN;
        return Number.isNaN(t) ? -Infinity : t;
    };
    return [...srmsList, ...ppaList.filter(r => !srmsUuids.has(r.reportUUID))]
        .sort((a, b) => createdAtMs(b) - createdAtMs(a));
});

export const createReport = createAsyncThunk('report/createReport', async payload => {
    const isSsp = payload.reportProductType === 'SSP';
    if (isSsp) {
        const response = await srmsApi.createReport(toReportTo(payload));
        return response.data;
    }
    const response = await ppa.sendReport(payload);
    return response.data;
});

export const fetchReport = createAsyncThunk('report/fetchReport', async (reportUUID, { getState }) => {
    if (sourceOf(getState, reportUUID) === SOURCE.SRMS) {
        const response = await srmsApi.getReport(reportUUID);
        return fromReportTo(response.data);
    }
    const response = await ppa.getReport(reportUUID);
    return response.data;
});

// Fetch an SRMS report's full config the first time it's opened for editing (the list is metadata only).
export const hydrateReport = createAsyncThunk('report/hydrateReport', async ({ reportUUID, sitesAndAppsUUIDs }) => {
    const response = await srmsApi.getReport(reportUUID);
    return { report: fromReportTo(response.data), sitesAndAppsUUIDs };
});

export const updateReport = createAsyncThunk('report/updateReport', async (payload, { getState }) => {
    if (sourceOf(getState, payload.reportUUID) === SOURCE.SRMS) {
        const response = await srmsApi.updateReport(toReportTo(payload));
        return response.data;
    }
    const response = await ppa.setReport(payload);
    return response.data;
});

export const deleteReport = createAsyncThunk('report/deleteReport', async (reportUUID, { getState }) => {
    if (sourceOf(getState, reportUUID) === SOURCE.SRMS) {
        const response = await srmsApi.deleteReport(reportUUID);
        return response.data;
    }
    const response = await ppa.removeReport(reportUUID);
    return response.data;
});

// Poll a queued SRMS generation until it finishes; large (MSQ) reports can take up to ~20 minutes.
const JOB_POLL_MS = 3000;
const JOB_TIMEOUT_MS = 20 * 60 * 1000;
const waitForReportJob = async jobId => {
    const deadline = Date.now() + JOB_TIMEOUT_MS;
    while (Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, JOB_POLL_MS));
        const { data } = await srmsApi.getReportJob(jobId);
        if (data.status === 'READY') return;
        if (data.status === 'FAILED') throw new Error(data.error || 'Report generation failed');
    }
    throw new Error('Report generation timed out');
};

export const downloadReport = createAsyncThunk('report/downloadReport', async (reportUUID, { getState }) => {
    const report = (getState().report.allReports || []).find(r => r.reportUUID === reportUUID);
    const source = (report && report._source) || SOURCE.PPA;

    if (source === SOURCE.SRMS) {
        // SRMS queues the generation: submit, wait for it, then fetch the file. Fetch the config first if not
        // hydrated; SRMS re-scopes it server-side.
        const saved = (report && report._srmsRaw) || (await srmsApi.getReport(reportUUID)).data;
        // A saved SRMS report has no format (reports_v2 has no column), so set CSV as SPF does.
        const body = { ...saved, reportFormat: 'CSV' };
        const { data: job } = await srmsApi.createReportJob(body);
        // SRMS hands back an identical job from the last hour if there is one, possibly already READY.
        if (job.status !== 'READY') await waitForReportJob(job.jobId);
        const response = await srmsApi.getReportJobFile(job.jobId);
        const filename = filenameFromDisposition(response.headers['content-disposition']) || `${(report && report.name) || 'report'}.csv`;
        return { source: SOURCE.SRMS, blob: response.data, filename };
    }

    // PPA: asynchronous — the file may still be generating; when ready it returns a signed URL.
    const response = await ppa.downloadReport(reportUUID);
    if (response.data.status === 'processing') {
        return { source: SOURCE.PPA, processing: true };
    }
    return { source: SOURCE.PPA, url: response.data.url };
});

export const getPreviewReport = createAsyncThunk('report/getPreviewReport', async payload => {
    const format = payload.schedule?.format;
    const isSsp = payload.reportProductType === 'SSP'; // same backend as create

    if (isSsp) {
        const body = toReportTo(payload);
        if (format === 'JSON') {
            const response = await srmsApi.previewReport(body);
            return response.data;
        }
        // Download from the preview page = the full report, queued like a saved report's download (CSV only).
        const { data: job } = await srmsApi.createReportJob({ ...body, reportFormat: 'CSV' });
        if (job.status !== 'READY') await waitForReportJob(job.jobId);
        const response = await srmsApi.getReportJobFile(job.jobId);
        const filename = filenameFromDisposition(response.headers['content-disposition']) || `${payload.name || 'report'}.csv`;
        return { data: response.data, filename };
    }

    // PPA (unchanged)
    if (format === 'JSON') {
        const response = await ppa.getPreviewReport(payload);
        return response.data
    } else if (format === 'CSV') {
        const response = await ppa.getPreviewFile(payload);
        const filename = response.headers['content-disposition'].split(';')[1].replace('filename=', '').trim()

        return ({ data: response.data, filename })
    }
    else if (format === 'XLSX') {
        const response = await ppa.getPreviewFile(payload);
        const filename = response.headers['content-disposition']
            .split(';')
            .find(part => part.includes('filename'))
            .replace('filename=', '')
            .trim();

        // Ensure the file extension is .xlsx
        const adjustedFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
        return ({ data: response.data, filename: adjustedFilename });
    }
})
